import { Autocomplete, MenuItem, Stack, TextField } from "@mui/material";
import {
  BusinessEntityType,
  AssetStatus,
  CustomFieldContext,
  GetGoodProfileListQuery,
  StorageFacility,
  StorageFacilityUnit,
  useGetGoodProfileListQuery,
} from "../../../graphql/generated";
import NumberTextField from "../../common/NumberTextField";
import EnumSelect from "../../common/EnumSelect";
import BusinessEntitySelectContainer from "../../shipment/ShipmentForm/BusinessEntitySelect";
import toDefaultPrecision, {
  parseFloatWithDefaultPrecision,
} from "../../../utils/conversion/numberPrecision";
import { castArray, sum } from "lodash";
import CustomFieldsFormContainer from "../../extensions/CustomFieldsForm";
import { useTranslation } from "react-i18next";
import { testIds } from "./StorageFacilityForm.testIds";

type StorageFacilityFormProps = {
  storageFacility: Partial<StorageFacility>;
  otherStorageFacilities?: Omit<StorageFacility, "_id">[];
  onChange: (storageFacility: Partial<StorageFacility>) => void;
  goodProfiles: GetGoodProfileListQuery["goodProfiles"]["data"];
};

const StorageFacilityForm = ({
  storageFacility,
  otherStorageFacilities,
  onChange,
  goodProfiles,
}: StorageFacilityFormProps) => {
  const { t } = useTranslation(["common", "assets"]);
  const selectedGoodProfiles = goodProfiles.filter(
    (good) => storageFacility.commodityIds?.includes(good._id) || false
  );
  const selectableGoodProfiles = goodProfiles.filter(
    (good) =>
      good.status !== AssetStatus.Inactive ||
      storageFacility.commodityIds?.includes(good._id)
  );

  const isUsedInManifold = !!otherStorageFacilities?.some(
    (otherStorageFacility) =>
      storageFacility.identifier &&
      otherStorageFacility.linkedStorageFacilityIds?.includes(
        storageFacility.identifier
      )
  );
  const isManifold =
    (storageFacility.linkedStorageFacilityIds?.length || 0) > 0;
  // Get primary commodity (first in the list) and additional commodities
  const primaryCommodity =
    selectedGoodProfiles.length > 0 ? selectedGoodProfiles[0] : null;
  const additionalCommodities = selectedGoodProfiles.slice(1);

  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{ mb: 2, mt: 1 }}
      alignItems={"flex-start"}
    >
      <Stack spacing={1}>
        <TextField
          label="Identifier"
          sx={{
            flex: 0.5,
          }}
          required
          name="storageFacility.identifier"
          value={storageFacility.identifier || ""}
          disabled={isUsedInManifold}
          onChange={(event) => {
            onChange({
              ...storageFacility,
              identifier: event.target.value,
            });
          }}
        inputProps={{ "data-testid": testIds.storageFacilityFormIdentifier } as React.InputHTMLAttributes<HTMLInputElement>}
        />

        <TextField
          label="Manifold of"
          placeholder="Manifold of"
          select
          SelectProps={{
            multiple: true,
          }}
          disabled={isUsedInManifold}
          value={storageFacility.linkedStorageFacilityIds || []}
          onChange={(event) => {
            const linkedStorageFacilityIds = castArray(event.target.value);
            const linkedStorageFacilities = otherStorageFacilities?.filter(
              (otherStorageFacility) =>
                linkedStorageFacilityIds.includes(
                  otherStorageFacility.identifier
                )
            );
            const firstStorageFacility = linkedStorageFacilities?.[0];
            onChange({
              ...storageFacility,
              linkedStorageFacilityIds: castArray(event.target.value),
              capacity: sum(
                linkedStorageFacilities?.map(
                  (linkedStorageFacility) => linkedStorageFacility.capacity || 0
                ) || []
              ),
              shutDownLevel: sum(
                linkedStorageFacilities?.map(
                  (linkedStorageFacility) =>
                    linkedStorageFacility.shutDownLevel || 0
                ) || []
              ),
              safeFillLevel: sum(
                linkedStorageFacilities?.map(
                  (linkedStorageFacility) =>
                    linkedStorageFacility.safeFillLevel || 0
                ) || []
              ),
              commodityIds: firstStorageFacility?.commodityIds || [],
              unit: firstStorageFacility?.unit,
              defaultSupplierId: firstStorageFacility?.defaultSupplierId,
            });
          }}
        inputProps={{ "data-testid": testIds.storageFacilityFormManifoldOf } as React.InputHTMLAttributes<HTMLInputElement>}
        >
          {(otherStorageFacilities || []).map((otherStorageFacility) => {
            const otherStorageFacilityPrimaryCommodity = goodProfiles.find(
              (gp) => gp._id === otherStorageFacility.commodityIds[0]
            );

            return (
              <MenuItem data-testid={`${testIds.storageFacilityFormManifoldOfOption}-${otherStorageFacility.identifier}`}
                value={otherStorageFacility.identifier}
                key={otherStorageFacility.identifier}
                disabled={
                  !!(
                    // Check if the primary commodity matches
                    (
                      primaryCommodity &&
                      otherStorageFacility.commodityIds &&
                      otherStorageFacility.commodityIds.length > 0 &&
                      otherStorageFacilityPrimaryCommodity &&
                      otherStorageFacilityPrimaryCommodity._id !==
                        primaryCommodity._id
                    )
                  )
                }
              >
                {otherStorageFacility.identifier}
              </MenuItem>
            );
          })}
        </TextField>
      </Stack>
      <Stack spacing={1} flex={1}>
        <NumberTextField
          label="Capacity"
          fullWidth
          required
          name="storageFacility.capacity"
          value={String(storageFacility.capacity || "")}
          onChange={(event) => {
            onChange({
              ...storageFacility,
              capacity: parseFloat(event.target.value),
            });
          }}
        />
        <Stack direction="column" spacing={1}>
          <Stack direction="row">
            <NumberTextField
              label="Shut Down Amount"
              fullWidth
              name="storageFacility.shutDownLevel"
              value={String(storageFacility.shutDownLevel || "")}
              onChange={(event) => {
                onChange({
                  ...storageFacility,
                  shutDownLevel: parseFloatWithDefaultPrecision(
                    event.target.value
                  ),
                });
              }}
            />
            <NumberTextField
              label="%"
              name="storageFacility.shutDownLevelPercentage"
              value={toDefaultPrecision(
                ((storageFacility.shutDownLevel || 0) /
                  (storageFacility.capacity || 1)) *
                  100
              ).toString()}
              onChange={(event) => {
                const shutDownLevelPercentage = parseFloat(event.target.value);
                onChange({
                  ...storageFacility,
                  shutDownLevel: toDefaultPrecision(
                    ((shutDownLevelPercentage || 0) / 100) *
                      (storageFacility.capacity || 1)
                  ),
                });
              }}
            />
          </Stack>
          <Stack direction="row">
            <NumberTextField
              label="Safe Fill Amount"
              fullWidth
              name="storageFacility.safeFillLevel"
              value={String(storageFacility.safeFillLevel || "")}
              onChange={(event) => {
                onChange({
                  ...storageFacility,
                  safeFillLevel: parseFloatWithDefaultPrecision(
                    event.target.value
                  ),
                });
              }}
            />
            <NumberTextField
              label="%"
              name="storageFacility.safeFillLevelPercentage"
              value={toDefaultPrecision(
                ((storageFacility.safeFillLevel || 0) /
                  (storageFacility.capacity || 1)) *
                  100
              ).toString()}
              onChange={(event) => {
                const safeFillLevelPercentage = parseFloat(event.target.value);
                onChange({
                  ...storageFacility,
                  safeFillLevel: toDefaultPrecision(
                    ((safeFillLevelPercentage || 0) / 100) *
                      (storageFacility.capacity || 1)
                  ),
                });
              }}
            />
          </Stack>
        </Stack>
      </Stack>
      <EnumSelect data-testid="storage-facility-form-storage-facility-unit"
        enumObject={StorageFacilityUnit}
        label="UOM"
        fullWidth
        required
        name="storageFacility.unit"
        disabled={isUsedInManifold || isManifold}
        value={storageFacility.unit}
        onChange={(event, value) => {
          if (!value) {
            return;
          }
          onChange({
            ...storageFacility,
            unit: value,
          });
        }}
        sx={{
          flex: 0.5,
        }}
      />

      {goodProfiles ? (
        <Stack spacing={1} sx={{ flex: 1 }}>
          {/* Primary Commodity - Single Select */}
          <Autocomplete data-testid={testIds.storageFacilityFormPrimaryCommodityPicker}
            options={selectableGoodProfiles}
            value={primaryCommodity}
            disabled={isUsedInManifold || isManifold}
            getOptionLabel={(goodProfile) => goodProfile.label}
            getOptionDisabled={(goodProfile) => {
              const selectedGoodProfilesClassIds = selectedGoodProfiles.map(
                (goodProfile) => goodProfile.goodProfileClassId
              );
              if (!selectedGoodProfilesClassIds.length) {
                return false;
              }
              if (
                !selectedGoodProfilesClassIds.includes(
                  goodProfile.goodProfileClassId
                ) ||
                !goodProfile.goodProfileClassId
              ) {
                return true;
              }

              return false;
            }}
            size="small"
            renderInput={(inputParams) => (
              <TextField
                {...inputParams}
                required
                fullWidth
                name="primaryCommodity"
                label="Primary Commodity"
                size="medium"
                inputProps={{ ...inputParams.inputProps, "data-testid": testIds.storageFacilityFormPrimaryCommoditySearch }}
              />
            )}
            onChange={(event, value) => {
              // If there's no value, clear all commodities
              if (!value) {
                onChange({
                  ...storageFacility,
                  commodityIds: [],
                });
                return;
              }

              // Preserve additional commodities but set new primary commodity
              const newCommodityIds = [
                value._id,
                ...additionalCommodities.map((c) => c._id),
              ];
              onChange({
                ...storageFacility,
                commodityIds: newCommodityIds,
              });
            }}
          />

          {/* Additional Commodities - Multi Select */}
          <Autocomplete data-testid={testIds.storageFacilityFormAdditionalCommoditiesPicker}
            options={selectableGoodProfiles}
            value={additionalCommodities}
            disabled={isUsedInManifold || isManifold}
            getOptionLabel={(goodProfile) => goodProfile.label}
            getOptionDisabled={(goodProfile) => {
              // Disable if it's already the primary commodity
              if (
                primaryCommodity &&
                goodProfile._id === primaryCommodity._id
              ) {
                return true;
              }

              // Ensure all commodities have the same class ID
              if (
                primaryCommodity &&
                goodProfile.goodProfileClassId !==
                  primaryCommodity.goodProfileClassId
              ) {
                return true;
              }

              const selectedGoodProfilesClassIds = selectedGoodProfiles.map(
                (goodProfile) => goodProfile.goodProfileClassId
              );
              if (
                !selectedGoodProfilesClassIds.includes(
                  goodProfile.goodProfileClassId
                ) ||
                !goodProfile.goodProfileClassId
              ) {
                return true;
              }

              return false;
            }}
            multiple
            size="small"
            renderInput={(inputParams) => (
              <TextField
                {...inputParams}
                fullWidth
                name="additionalCommodities"
                label="Additional Commodities"
                size="medium"
                inputProps={{ ...inputParams.inputProps, "data-testid": testIds.storageFacilityFormAdditionalCommoditiesSearch }}
              />
            )}
            onChange={(event, value) => {
              // Combine primary commodity with additional commodities
              const newCommodityIds = primaryCommodity
                ? [
                    primaryCommodity._id,
                    ...value.map((goodProfile) => goodProfile._id),
                  ]
                : value.map((goodProfile) => goodProfile._id);

              onChange({
                ...storageFacility,
                commodityIds: newCommodityIds,
              });
            }}
          />
        </Stack>
      ) : null}

      <BusinessEntitySelectContainer data-testid="storage-facility-form-supplier-select"
        inputProps={{
          label: "Supplier",
          size: "medium",
          required: false,
        }}
        sx={{
          flex: 1,
        }}
        value={storageFacility.defaultSupplierId || null}
        businessEntityType={BusinessEntityType.Supplier}
        onChange={(businessEntityId) => {
          onChange({
            ...storageFacility,
            defaultSupplierId: businessEntityId,
          });
        }}
        filterBusinessEntityIds={(
          [
            ...(primaryCommodity ? [primaryCommodity] : []),
            ...additionalCommodities,
          ].flatMap((goodProfile) => goodProfile.supplierIds || []) || []
        ).concat(storageFacility.defaultSupplierId || [])}
      />

      <CustomFieldsFormContainer
        context={CustomFieldContext.StorageFacility}
        customFields={storageFacility.customFields || []}
        onChange={(customFields) => {
          onChange({
            ...storageFacility,
            customFields,
          });
        }}
      />
      <NumberTextField
        label={t(
          "common:plannedDwellTimeInMinutes",
          "Planned Dwell Time in Minutes"
        )}
        name="storageFacility.plannedDwellTimeInMinutes"
        value={String(storageFacility.plannedDwellTimeInMinutes ?? "")}
        onChange={(event) => {
          onChange({
            ...storageFacility,
            plannedDwellTimeInMinutes: event.target.value
              ? Number(event.target.value)
              : null,
          });
        }}
      />
    </Stack>
  );
};

type StorageFacilityFormContainerProps = Omit<
  StorageFacilityFormProps,
  "goodProfiles"
>;

const StorageFacilityFormContainer = (
  props: StorageFacilityFormContainerProps
) => {
  const goodProfilesQuery = useGetGoodProfileListQuery({ includeInactive: true });
  const goodProfiles = goodProfilesQuery.data?.goodProfiles.data || [];
  return <StorageFacilityForm {...props} goodProfiles={goodProfiles} />;
};

export default StorageFacilityFormContainer;

export { StorageFacilityForm as StorageFacilityFormComponent };
